import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/auth-provider';
import { useDocumentTitle } from '../hooks/use-document-title';
import { LogoMark } from '../logo/logo-mark';
import { AppButton } from '../button/app-button';
import {
  BrandRow,
  Card,
  Heading,
  Shell,
  Subheading,
  Wordmark,
} from '../onboarding/onboarding.styled';

/**
 * THE ORGANISATION IS SUSPENDED.
 *
 * When Lobbero suspends an organisation the server refuses every signed-in
 * request from everybody in it with 403 ORG_SUSPENDED. Nothing in the app knew
 * that answer: an open tab kept its last screen and failed one panel at a time,
 * a reload fell through to the SIGN-IN FORM with no word of why, and signing in
 * "worked" and landed on a home whose every request was refused.
 *
 * This is the screen for that: what has happened, who can undo it, and the two
 * things a person can do. It replaces the whole app (App.tsx) and is not a
 * route, so it cannot be bookmarked or reached while the organisation is live.
 *
 * The session is deliberately LEFT ALONE. A suspension is lifted by Lobbero,
 * not by signing in again, so when it is lifted "Try again" (or the quiet
 * re-check below) puts the person straight back where they were.
 */

export const SUSPENDED_TITLE = 'This organization is suspended';
export const SUSPENDED_TEXT =
  'Nobody in it can use Lobbero until it is restored. Nothing has been deleted. If you think this is a mistake, email hello@lobbero.com.';
export const SUSPENDED_STILL = 'It is still suspended.';

// How often the screen asks again by itself while it is open.
const RECHECK_MS = 60 * 1000;

export const OrgSuspended: React.FC = () => {
  const { logout, refreshAuth } = useAuth();
  const [checking, setChecking] = useState(false);
  const [asked, setAsked] = useState(false);
  useDocumentTitle('Organization suspended');

  useEffect(() => {
    const timer = window.setInterval(() => {
      refreshAuth();
    }, RECHECK_MS);

    return () => window.clearInterval(timer);
  }, [refreshAuth]);

  // Still mounted after the answer means the answer was "suspended" again:
  // a restored organisation unmounts this screen.
  const tryAgain = async () => {
    setChecking(true);
    try {
      await refreshAuth();
    } finally {
      setChecking(false);
      setAsked(true);
    }
  };

  return (
    <Shell>
      <Card>
        <BrandRow>
          <LogoMark size={40} aria-hidden="true" />
          <Wordmark>Lobbero</Wordmark>
        </BrandRow>
        <Heading>{SUSPENDED_TITLE}</Heading>
        <Subheading>{SUSPENDED_TEXT}</Subheading>
        <div role="status" aria-live="polite">
          {asked && !checking ? <Subheading>{SUSPENDED_STILL}</Subheading> : null}
        </div>
        <AppButton onClick={tryAgain} loading={checking}>
          Try again
        </AppButton>
        <AppButton variant="secondary" onClick={() => logout()}>
          Sign out
        </AppButton>
      </Card>
    </Shell>
  );
};
